import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Shield, Megaphone, FileText, Calendar, Settings, LogOut, Plus, Trash2, Eye, EyeOff, Users, BarChart3, X, ScrollText, Lightbulb, Check, ChevronDown, ChevronUp, MessageSquare, Mail, Network } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import HolidayOverlay from "@/components/HolidayOverlay";
import SeasonalOverlay from "@/components/SeasonalOverlay";
import { useCMod } from "@/hooks/useCMod";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ModeratorPortal() {
  const navigate = useNavigate();
  const { disableCMod } = useCMod();
  const [currentUser, setCurrentUser] = useState(null);
  const [announcements, setAnnouncements] = useState([]);
  const [allAnnouncements, setAllAnnouncements] = useState([]);
  const [showAnnouncementForm, setShowAnnouncementForm] = useState(false);
  const [newAnnouncement, setNewAnnouncement] = useState({ title: "", message: "" });
  const [loading, setLoading] = useState(false);
  
  // Poll notification states
  const [hasNewPolls, setHasNewPolls] = useState(false);
  const [newPollCount, setNewPollCount] = useState(0);
  const [showPollNotification, setShowPollNotification] = useState(true);
  
  // Dismissed announcements tracking
  const [dismissedAnnouncements, setDismissedAnnouncements] = useState([]);
  const [showDismissed, setShowDismissed] = useState(false);
  
  // Feature requests
  const [featureRequests, setFeatureRequests] = useState([]);
  const [showFeatureForm, setShowFeatureForm] = useState(false);
  const [newFeatureRequest, setNewFeatureRequest] = useState({ title: "", description: "", category: "general" });
  const [featureRequestsExpanded, setFeatureRequestsExpanded] = useState(true);
  const [emailPromptOpen, setEmailPromptOpen] = useState(false);
  const [emailPromptValue, setEmailPromptValue] = useState("");
  const [emailPromptLoading, setEmailPromptLoading] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('moderator_token');
    const role = localStorage.getItem('moderator_role');
    const username = localStorage.getItem('moderator_username');
    
    if (!token) {
      navigate('/moderator/login');
      return;
    }
    
    setCurrentUser({ role, username, token });
    fetchAnnouncements();
    fetchDismissedAnnouncements();
    fetchFeatureRequests();
    checkNewPolls();
    checkForMissingEmail();
  }, [navigate]);

  useEffect(() => {
    if (currentUser && (currentUser.role === 'admin' || currentUser.role === 'mmod')) {
      fetchAllAnnouncements();
    }
  }, [currentUser]);

  // Announcement functions
  const fetchAnnouncements = async () => {
    try {
      const response = await axios.get(`${API}/announcements`);
      setAnnouncements(response.data);
    } catch (error) {
      console.error("Failed to fetch announcements:", error);
    }
  };

  const fetchAllAnnouncements = async () => {
    try {
      const token = localStorage.getItem('moderator_token');
      const response = await axios.get(`${API}/announcements/all`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAllAnnouncements(response.data);
    } catch (error) {
      console.error("Failed to fetch all announcements:", error);
    }
  };

  const handleCreateAnnouncement = async (e) => {
    e.preventDefault();
    if (!newAnnouncement.title || !newAnnouncement.message) {
      toast.error("Please fill in all fields");
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('moderator_token');
      await axios.post(`${API}/announcements`, newAnnouncement, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Announcement created successfully!");
      setNewAnnouncement({ title: "", message: "" });
      setShowAnnouncementForm(false);
      fetchAnnouncements();
      fetchAllAnnouncements();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create announcement");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAnnouncement = async (id) => {
    if (!window.confirm("Are you sure you want to delete this announcement?")) return;

    try {
      const token = localStorage.getItem('moderator_token');
      await axios.delete(`${API}/announcements/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Announcement deleted");
      fetchAnnouncements();
      fetchAllAnnouncements();
    } catch (error) {
      toast.error("Failed to delete announcement");
    }
  };

  const handleToggleAnnouncement = async (id) => {
    try {
      const token = localStorage.getItem('moderator_token');
      await axios.patch(`${API}/announcements/${id}/toggle`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Announcement visibility toggled");
      fetchAnnouncements();
      fetchAllAnnouncements();
    } catch (error) {
      toast.error("Failed to toggle announcement");
    }
  };

  // Dismissed announcements functions
  const fetchDismissedAnnouncements = async () => {
    try {
      const token = localStorage.getItem('moderator_token');
      const response = await axios.get(`${API}/announcements/dismissed`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDismissedAnnouncements(response.data);
    } catch (error) {
      console.error("Failed to fetch dismissed announcements:", error);
    }
  };

  const handleDismissAnnouncement = async (id) => {
    try {
      const token = localStorage.getItem('moderator_token');
      await axios.post(`${API}/announcements/${id}/dismiss`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDismissedAnnouncements(prev => [...prev, id]);
      toast.success("Announcement marked as read");
    } catch (error) {
      toast.error("Failed to dismiss announcement");
    }
  };

  const handleUndismissAnnouncement = async (id) => {
    try {
      const token = localStorage.getItem('moderator_token');
      await axios.post(`${API}/announcements/${id}/undismiss`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDismissedAnnouncements(prev => prev.filter(a => a !== id));
      toast.success("Announcement restored");
    } catch (error) {
      toast.error("Failed to restore announcement");
    }
  };

  // Feature request functions
  const fetchFeatureRequests = async () => {
    try {
      const token = localStorage.getItem('moderator_token');
      const response = await axios.get(`${API}/feature-requests`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFeatureRequests(response.data);
    } catch (error) {
      console.error("Failed to fetch feature requests:", error);
    }
  };

  const checkForMissingEmail = async () => {
    try {
      const token = localStorage.getItem('moderator_token');
      const response = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.needs_email) {
        setEmailPromptOpen(true);
      }
    } catch (error) {
      console.error("Failed to check moderator email status:", error);
    }
  };

  const handleEmailPromptSubmit = async (e) => {
    e.preventDefault();
    setEmailPromptLoading(true);
    try {
      const token = localStorage.getItem('moderator_token');
      await axios.post(
        `${API}/auth/set-email`,
        { email: emailPromptValue },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Email saved successfully!");
      setEmailPromptOpen(false);
    } catch (error) {
      console.error(error);
      toast.error(error.response?.data?.detail || "Failed to save email");
    } finally {
      setEmailPromptLoading(false);
    }
  };

  const handleCreateFeatureRequest = async (e) => {
    e.preventDefault();
    if (!newFeatureRequest.title || !newFeatureRequest.description) {
      toast.error("Please fill in title and description");
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('moderator_token');
      await axios.post(`${API}/feature-requests`, newFeatureRequest, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Feature request submitted successfully!");
      setNewFeatureRequest({ title: "", description: "", category: "general" });
      setShowFeatureForm(false);
      fetchFeatureRequests();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to submit feature request");
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateFeatureRequest = async (id, status, adminNotes = null) => {
    try {
      const token = localStorage.getItem('moderator_token');
      const updateData = { status };
      if (adminNotes !== null) {
        updateData.admin_notes = adminNotes;
      }
      await axios.patch(`${API}/feature-requests/${id}`, updateData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Feature request updated");
      fetchFeatureRequests();
    } catch (error) {
      toast.error("Failed to update feature request");
    }
  };

  const handleDeleteFeatureRequest = async (id) => {
    if (!window.confirm("Are you sure you want to delete this feature request?")) return;
    
    try {
      const token = localStorage.getItem('moderator_token');
      await axios.delete(`${API}/feature-requests/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Feature request deleted");
      fetchFeatureRequests();
    } catch (error) {
      toast.error("Failed to delete feature request");
    }
  };

  // Poll notification functions
  const checkNewPolls = async () => {
    try {
      const token = localStorage.getItem('moderator_token');
      const response = await axios.get(`${API}/polls/check-new`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setHasNewPolls(response.data.has_new_polls);
      setNewPollCount(response.data.count);
      setShowPollNotification(response.data.has_new_polls);
    } catch (error) {
      console.error("Failed to check new polls:", error);
    }
  };

  const handleLogout = () => {
    disableCMod(); // Disable CMod mode on logout
    localStorage.clear();
    toast.success("Logged out successfully");
    navigate('/');
  };

  const getRoleBadge = (role) => {
    const config = {
      admin: { color: "bg-red-500/20 text-red-400 border-red-500/50", label: "ADMIN" },
      mmod: { color: "bg-red-500/20 text-red-500 border-red-500/50", label: "MMOD" },
      moderator: { color: "bg-blue-500/20 text-blue-400 border-blue-500/50", label: "MODERATOR" },
      lmod: { color: "bg-purple-500/20 text-purple-400 border-purple-500/50", label: "LMOD" },
      smod: { color: "bg-pink-500/20 text-pink-400 border-pink-500/50", label: "SMOD" },
      developer: { color: "bg-yellow-500/20 text-yellow-400 border-yellow-500/50", label: "DEVELOPER" }
    };
    const roleConfig = config[role] || { color: "bg-slate-500/20 text-slate-400 border-slate-500/50", label: role };
    return <Badge className={`${roleConfig.color} uppercase font-semibold`}>{roleConfig.label}</Badge>;
  };

  if (!currentUser) {
    return null;
  }

  const canManageAnnouncements = currentUser.role === 'admin' || currentUser.role === 'mmod';
  const canViewFeatureRequests = currentUser.role === 'admin' || currentUser.role === 'mmod' || currentUser.role === 'developer';

  const getStatusBadge = (status) => {
    const config = {
      pending: { color: "bg-slate-500/20 text-slate-400 border-slate-500/50", label: "Pending" },
      reviewed: { color: "bg-blue-500/20 text-blue-400 border-blue-500/50", label: "Reviewed" },
      approved: { color: "bg-emerald-500/20 text-emerald-400 border-emerald-500/50", label: "Approved" },
      rejected: { color: "bg-red-500/20 text-red-400 border-red-500/50", label: "Rejected" },
      implemented: { color: "bg-purple-500/20 text-purple-400 border-purple-500/50", label: "Implemented" }
    };
    const statusConfig = config[status] || config.pending;
    return <Badge className={`${statusConfig.color} text-xs`}>{statusConfig.label}</Badge>;
  };

  const getCategoryBadge = (category) => {
    const config = {
      general: { color: "bg-slate-600/30 text-slate-400", label: "General" },
      ui: { color: "bg-cyan-500/20 text-cyan-400", label: "UI" },
      functionality: { color: "bg-amber-500/20 text-amber-400", label: "Functionality" },
      bug: { color: "bg-red-500/20 text-red-400", label: "Bug" },
      other: { color: "bg-slate-600/30 text-slate-400", label: "Other" }
    };
    const catConfig = config[category] || config.general;
    return <Badge className={`${catConfig.color} text-xs`}>{catConfig.label}</Badge>;
  };

  return (
    <div className="min-h-screen bg-slate-950 text-slate-200 grid-texture">
      {/* Holiday Animation Overlay (overrides seasonal when active) */}
      <HolidayOverlay />
      {/* Seasonal Animation Overlay */}
      <SeasonalOverlay />

      <Dialog open={emailPromptOpen}>
        <DialogContent className="bg-slate-900 border-slate-700 text-slate-200 sm:max-w-md" data-testid="email-confirmation-dialog">
          <DialogHeader>
            <DialogTitle className="text-amber-400 flex items-center gap-2">
              <Mail className="h-5 w-5" />
              Confirm your email
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              Please enter a valid email address. We only use this to help you reset your password in the future.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleEmailPromptSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email-prompt" className="text-slate-300 font-medium">
                Email Address
              </Label>
              <Input
                id="email-prompt"
                name="email-prompt"
                type="email"
                value={emailPromptValue}
                onChange={(e) => setEmailPromptValue(e.target.value)}
                required
                className="bg-slate-950/60 border-slate-700 focus:border-amber-500 focus:ring-1 focus:ring-amber-500 text-slate-200 rounded-sm"
                placeholder="Enter your email address"
              />
            </div>
            <Button
              type="submit"
              disabled={emailPromptLoading}
              className="w-full bg-amber-500 hover:bg-amber-600 text-white font-bold uppercase tracking-wide py-5 text-base rounded-sm btn-glow"
            >
              {emailPromptLoading ? "Saving..." : "Save Email"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
      
      {/* User Info Bar */}
      <div className="bg-slate-900/50 border-b border-slate-800 py-2 px-3 sm:px-6">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="text-xs sm:text-sm text-slate-400">Welcome, <span className="text-amber-400">{currentUser.username}</span></span>
            {getRoleBadge(currentUser.role)}
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto py-4 sm:py-8 px-3 sm:px-6">
        {/* Quick Actions */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-7 gap-2 sm:gap-4 mb-4 sm:mb-8">
          <Button
            onClick={() => navigate('/moderator/dashboard')}
            className="bg-amber-500 hover:bg-amber-600 text-white font-bold uppercase tracking-wide py-4 sm:py-6 rounded-sm btn-glow text-xs sm:text-sm"
          >
            <FileText className="h-4 w-4 sm:h-5 sm:w-5 mr-1 sm:mr-2" />
            Apps
          </Button>
          <Button
            onClick={() => navigate('/moderator/server-assignments')}
            variant="outline"
            className="border-2 border-emerald-500 text-emerald-500 hover:bg-emerald-500/20 font-bold uppercase tracking-wide py-4 sm:py-6 rounded-sm text-xs sm:text-sm"
          >
            <Calendar className="h-4 w-4 sm:h-5 sm:w-5 mr-1 sm:mr-2" />
            Servers
          </Button>
          <Button
            onClick={() => navigate('/moderator/polls')}
            variant="outline"
            className={`border-2 font-bold uppercase tracking-wide py-4 sm:py-6 rounded-sm text-xs sm:text-sm ${
              hasNewPolls 
                ? 'border-cyan-400 text-cyan-400 hover:bg-cyan-500/20 animate-pulse' 
                : 'border-cyan-500 text-cyan-500 hover:bg-cyan-500/20'
            }`}
          >
            <BarChart3 className="h-4 w-4 sm:h-5 sm:w-5 mr-1 sm:mr-2" />
            Polls
            {hasNewPolls && <span className="ml-1 w-2 h-2 bg-red-500 rounded-full"></span>}
          </Button>
          <Button
            onClick={() => navigate('/moderator/settings')}
            variant="outline"
            className="border-2 border-slate-600 text-slate-300 hover:bg-slate-800 font-bold uppercase tracking-wide py-4 sm:py-6 rounded-sm text-xs sm:text-sm"
          >
            <Settings className="h-4 w-4 sm:h-5 sm:w-5 mr-1 sm:mr-2" />
            Settings
          </Button>
          <Button
            onClick={() => navigate('/moderator/changelog')}
            variant="outline"
            className="border-2 border-purple-500 text-purple-400 hover:bg-purple-500/20 font-bold uppercase tracking-wide py-4 sm:py-6 rounded-sm text-xs sm:text-sm"
          >
            <ScrollText className="h-4 w-4 sm:h-5 sm:w-5 mr-1 sm:mr-2" />
            Changelog
          </Button>
          <Button
            onClick={() => navigate('/moderator/organogram')}
            variant="outline"
            className="border-2 border-rose-500 text-rose-400 hover:bg-rose-500/20 font-bold uppercase tracking-wide py-4 sm:py-6 rounded-sm text-xs sm:text-sm"
            data-testid="portal-organogram-btn"
          >
            <Network className="h-4 w-4 sm:h-5 sm:w-5 mr-1 sm:mr-2" />
            Org Chart
          </Button>
          <Button
            onClick={() => navigate('/apply')}
            variant="outline"
            className="border-2 border-blue-500 text-blue-400 hover:bg-blue-500/20 font-bold uppercase tracking-wide py-4 sm:py-6 rounded-sm text-xs sm:text-sm"
          >
            <Users className="h-4 w-4 sm:h-5 sm:w-5 mr-1 sm:mr-2" />
            Apply
          </Button>
        </div>

        {/* Poll Notification Popup */}
        {showPollNotification && hasNewPolls && (
          <div className="mb-4 sm:mb-8 p-4 bg-cyan-500/10 border border-cyan-500/50 rounded-lg relative animate-pulse">
            <button
              onClick={() => setShowPollNotification(false)}
              className="absolute top-2 right-2 text-slate-400 hover:text-white transition-colors"
            >
              <X className="h-5 w-5" />
            </button>
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-cyan-500/20 flex items-center justify-center">
                  <BarChart3 className="h-5 w-5 text-cyan-400" />
                </div>
                <div>
                  <h3 className="font-semibold text-cyan-400">New Poll{newPollCount > 1 ? 's' : ''} Available!</h3>
                  <p className="text-sm text-slate-400">
                    {newPollCount} poll{newPollCount > 1 ? 's' : ''} waiting for your vote
                  </p>
                </div>
              </div>
              <Button
                onClick={() => navigate('/moderator/polls')}
                className="bg-cyan-500 hover:bg-cyan-600 text-white rounded-sm ml-0 sm:ml-auto"
              >
                View Polls
              </Button>
            </div>
          </div>
        )}

        {/* Announcements Section */}
        <Card className="glass-card border-slate-700 mb-8">
          <CardHeader className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <CardTitle className="text-xl sm:text-2xl font-bold uppercase tracking-wide text-amber-500" style={{ fontFamily: 'Rajdhani, sans-serif' }}>
              <Megaphone className="inline-block mr-2 h-5 w-5 sm:h-6 sm:w-6" />
              Announcements
            </CardTitle>
            {canManageAnnouncements && (
              <Button
                onClick={() => setShowAnnouncementForm(!showAnnouncementForm)}
                className="bg-emerald-500 hover:bg-emerald-600 text-white rounded-sm text-sm w-full sm:w-auto"
              >
                <Plus className="h-4 w-4 mr-2" />
                New Announcement
              </Button>
            )}
          </CardHeader>
          <CardContent>
            {/* Create Announcement Form */}
            {showAnnouncementForm && canManageAnnouncements && (
              <form onSubmit={handleCreateAnnouncement} className="mb-6 p-4 bg-slate-900/50 rounded border border-slate-700">
                <div className="space-y-4">
                  <div>
                    <Label className="text-slate-300">Title</Label>
                    <Input
                      value={newAnnouncement.title}
                      onChange={(e) => setNewAnnouncement(prev => ({ ...prev, title: e.target.value }))}
                      placeholder="Announcement title..."
                      className="bg-slate-900/50 border-slate-700 text-slate-200 rounded-sm"
                    />
                  </div>
                  <div>
                    <Label className="text-slate-300">Message</Label>
                    <Textarea
                      value={newAnnouncement.message}
                      onChange={(e) => setNewAnnouncement(prev => ({ ...prev, message: e.target.value }))}
                      placeholder="Announcement message..."
                      className="bg-slate-900/50 border-slate-700 text-slate-200 rounded-sm min-h-[100px]"
                    />
                  </div>
                  <div className="flex gap-2">
                    <Button type="submit" disabled={loading} className="bg-emerald-500 hover:bg-emerald-600 text-white rounded-sm">
                      {loading ? "Creating..." : "Create Announcement"}
                    </Button>
                    <Button type="button" onClick={() => setShowAnnouncementForm(false)} variant="outline" className="border-slate-600 text-slate-300 rounded-sm">
                      Cancel
                    </Button>
                  </div>
                </div>
              </form>
            )}

            {/* Announcements List for Admins/MMODs */}
            {canManageAnnouncements ? (
              <div className="space-y-4">
                {allAnnouncements.length === 0 ? (
                  <p className="text-slate-500 text-center py-8">No announcements yet</p>
                ) : (
                  allAnnouncements.map((announcement) => (
                    <div key={announcement.id} className={`p-4 rounded border ${announcement.is_active ? 'bg-slate-900/50 border-slate-700' : 'bg-slate-900/30 border-slate-800 opacity-60'}`}>
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <h3 className="text-lg font-semibold text-amber-500">{announcement.title}</h3>
                            {!announcement.is_active && <Badge className="bg-slate-700 text-slate-400">Hidden</Badge>}
                          </div>
                          <p className="text-slate-300 whitespace-pre-wrap">{announcement.message}</p>
                          <p className="text-xs text-slate-500 mt-2">
                            Posted by {announcement.created_by} on {new Date(announcement.created_at).toLocaleDateString()}
                          </p>
                        </div>
                        <div className="flex gap-2 ml-4">
                          <Button
                            onClick={() => handleToggleAnnouncement(announcement.id)}
                            size="sm"
                            variant="outline"
                            className="border-slate-600 text-slate-300 hover:bg-slate-800"
                          >
                            {announcement.is_active ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                          </Button>
                          <Button
                            onClick={() => handleDeleteAnnouncement(announcement.id)}
                            size="sm"
                            className="bg-red-500/20 text-red-400 hover:bg-red-500/30"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            ) : (
              // Regular moderators see only active announcements with dismiss option
              <div className="space-y-4">
                {/* Active/Unread Announcements */}
                {announcements.filter(a => !dismissedAnnouncements.includes(a.id)).length === 0 && 
                 announcements.filter(a => dismissedAnnouncements.includes(a.id)).length === 0 ? (
                  <p className="text-slate-500 text-center py-8">No announcements</p>
                ) : (
                  <>
                    {announcements.filter(a => !dismissedAnnouncements.includes(a.id)).map((announcement) => (
                      <div key={announcement.id} className="p-4 bg-slate-900/50 rounded border border-amber-500/30 relative">
                        <div className="absolute top-2 right-2">
                          <Button
                            onClick={() => handleDismissAnnouncement(announcement.id)}
                            size="sm"
                            variant="ghost"
                            className="text-slate-400 hover:text-emerald-400 hover:bg-emerald-500/10"
                            title="Mark as read"
                          >
                            <Check className="h-4 w-4 mr-1" />
                            <span className="text-xs">Mark Read</span>
                          </Button>
                        </div>
                        <div className="flex items-center gap-2 mb-2">
                          <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/50 text-xs">NEW</Badge>
                          <h3 className="text-lg font-semibold text-amber-500">{announcement.title}</h3>
                        </div>
                        <p className="text-slate-300 whitespace-pre-wrap pr-24">{announcement.message}</p>
                        <p className="text-xs text-slate-500 mt-2">
                          Posted by {announcement.created_by} on {new Date(announcement.created_at).toLocaleDateString()}
                        </p>
                      </div>
                    ))}
                    
                    {/* Dismissed/Read Announcements - Collapsible */}
                    {announcements.filter(a => dismissedAnnouncements.includes(a.id)).length > 0 && (
                      <div className="mt-4">
                        <button
                          onClick={() => setShowDismissed(!showDismissed)}
                          className="flex items-center gap-2 text-slate-400 hover:text-slate-300 text-sm mb-2"
                        >
                          {showDismissed ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                          Read Announcements ({announcements.filter(a => dismissedAnnouncements.includes(a.id)).length})
                        </button>
                        
                        {showDismissed && (
                          <div className="space-y-2">
                            {announcements.filter(a => dismissedAnnouncements.includes(a.id)).map((announcement) => (
                              <div key={announcement.id} className="p-3 bg-slate-900/30 rounded border border-slate-800 opacity-70">
                                <div className="flex items-start justify-between">
                                  <div className="flex-1">
                                    <h3 className="text-base font-semibold text-slate-400">{announcement.title}</h3>
                                    <p className="text-slate-500 text-sm whitespace-pre-wrap line-clamp-2">{announcement.message}</p>
                                    <p className="text-xs text-slate-600 mt-1">
                                      Posted by {announcement.created_by} on {new Date(announcement.created_at).toLocaleDateString()}
                                    </p>
                                  </div>
                                  <Button
                                    onClick={() => handleUndismissAnnouncement(announcement.id)}
                                    size="sm"
                                    variant="ghost"
                                    className="text-slate-500 hover:text-amber-400"
                                    title="Mark as unread"
                                  >
                                    <Eye className="h-4 w-4" />
                                  </Button>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Feature Requests Section */}
        <Card className="glass-card border-slate-700 mb-8">
          <CardHeader 
            className="cursor-pointer select-none"
            onClick={() => setFeatureRequestsExpanded(!featureRequestsExpanded)}
          >
            <div className="flex items-center justify-between">
              <CardTitle className="text-xl sm:text-2xl font-bold uppercase tracking-wide text-purple-500" style={{ fontFamily: 'Rajdhani, sans-serif' }}>
                <Lightbulb className="inline-block mr-2 h-5 w-5 sm:h-6 sm:w-6" />
                Feature Requests
              </CardTitle>
              <div className="flex items-center gap-2">
                <Button
                  onClick={(e) => { e.stopPropagation(); setShowFeatureForm(!showFeatureForm); }}
                  className="bg-purple-500 hover:bg-purple-600 text-white rounded-sm text-sm"
                  size="sm"
                >
                  <Plus className="h-4 w-4 mr-1" />
                  Suggest
                </Button>
                {featureRequestsExpanded ? (
                  <ChevronUp className="h-5 w-5 text-slate-400" />
                ) : (
                  <ChevronDown className="h-5 w-5 text-slate-400" />
                )}
              </div>
            </div>
          </CardHeader>
          {featureRequestsExpanded && (
            <CardContent>
              {/* Create Feature Request Form */}
              {showFeatureForm && (
                <form onSubmit={handleCreateFeatureRequest} className="mb-6 p-4 bg-slate-900/50 rounded border border-purple-500/30">
                  <div className="space-y-4">
                    <div>
                      <Label className="text-slate-300">Title</Label>
                      <Input
                        value={newFeatureRequest.title}
                        onChange={(e) => setNewFeatureRequest(prev => ({ ...prev, title: e.target.value }))}
                        placeholder="Brief title for your suggestion..."
                        className="bg-slate-900/50 border-slate-700 text-slate-200 rounded-sm"
                      />
                    </div>
                    <div>
                      <Label className="text-slate-300">Category</Label>
                      <Select 
                        value={newFeatureRequest.category} 
                        onValueChange={(value) => setNewFeatureRequest(prev => ({ ...prev, category: value }))}
                      >
                        <SelectTrigger className="bg-slate-900/50 border-slate-700 text-slate-200 rounded-sm">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-slate-800 border-slate-700">
                          <SelectItem value="general">General</SelectItem>
                          <SelectItem value="ui">User Interface</SelectItem>
                          <SelectItem value="functionality">Functionality</SelectItem>
                          <SelectItem value="bug">Bug Report</SelectItem>
                          <SelectItem value="other">Other</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-slate-300">Description</Label>
                      <Textarea
                        value={newFeatureRequest.description}
                        onChange={(e) => setNewFeatureRequest(prev => ({ ...prev, description: e.target.value }))}
                        placeholder="Describe your feature request or suggestion in detail..."
                        className="bg-slate-900/50 border-slate-700 text-slate-200 rounded-sm min-h-[100px]"
                      />
                    </div>
                    <div className="flex gap-2">
                      <Button type="submit" disabled={loading} className="bg-purple-500 hover:bg-purple-600 text-white rounded-sm">
                        {loading ? "Submitting..." : "Submit Request"}
                      </Button>
                      <Button type="button" onClick={() => setShowFeatureForm(false)} variant="outline" className="border-slate-600 text-slate-300 rounded-sm">
                        Cancel
                      </Button>
                    </div>
                  </div>
                </form>
              )}

              {/* Feature Requests List */}
              {canViewFeatureRequests ? (
                // Admins/MMODs/Developers see all requests with management controls
                <div className="space-y-4">
                  {featureRequests.length === 0 ? (
                    <p className="text-slate-500 text-center py-8">No feature requests yet. Be the first to suggest!</p>
                  ) : (
                    featureRequests.map((request) => (
                      <div key={request.id} className="p-4 bg-slate-900/50 rounded border border-slate-700">
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2 flex-wrap">
                              <h3 className="text-base font-semibold text-purple-400">{request.title}</h3>
                              {getStatusBadge(request.status)}
                              {getCategoryBadge(request.category)}
                            </div>
                            <p className="text-slate-300 text-sm whitespace-pre-wrap">{request.description}</p>
                            <p className="text-xs text-slate-500 mt-2">
                              Submitted by <span className="text-slate-400">{request.submitted_by}</span> on {new Date(request.submitted_at).toLocaleDateString()}
                            </p>
                            {request.admin_notes && (
                              <div className="mt-2 p-2 bg-slate-800/50 rounded border border-slate-700">
                                <p className="text-xs text-slate-400">
                                  <MessageSquare className="inline h-3 w-3 mr-1" />
                                  Admin Note: <span className="text-slate-300">{request.admin_notes}</span>
                                </p>
                              </div>
                            )}
                          </div>
                          <div className="flex flex-col gap-2">
                            <Select 
                              value={request.status} 
                              onValueChange={(value) => handleUpdateFeatureRequest(request.id, value)}
                            >
                              <SelectTrigger className="bg-slate-800 border-slate-700 text-slate-200 rounded-sm text-xs w-28">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent className="bg-slate-800 border-slate-700">
                                <SelectItem value="pending">Pending</SelectItem>
                                <SelectItem value="reviewed">Reviewed</SelectItem>
                                <SelectItem value="approved">Approved</SelectItem>
                                <SelectItem value="rejected">Rejected</SelectItem>
                                <SelectItem value="implemented">Implemented</SelectItem>
                              </SelectContent>
                            </Select>
                            <Button
                              onClick={() => handleDeleteFeatureRequest(request.id)}
                              size="sm"
                              className="bg-red-500/20 text-red-400 hover:bg-red-500/30"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              ) : (
                // Regular moderators see only their own requests
                <div className="space-y-4">
                  {featureRequests.length === 0 ? (
                    <p className="text-slate-500 text-center py-8">You haven&apos;t submitted any feature requests yet.</p>
                  ) : (
                    featureRequests.map((request) => (
                      <div key={request.id} className="p-4 bg-slate-900/50 rounded border border-slate-700">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2 flex-wrap">
                              <h3 className="text-base font-semibold text-purple-400">{request.title}</h3>
                              {getStatusBadge(request.status)}
                              {getCategoryBadge(request.category)}
                            </div>
                            <p className="text-slate-300 text-sm whitespace-pre-wrap">{request.description}</p>
                            <p className="text-xs text-slate-500 mt-2">
                              Submitted on {new Date(request.submitted_at).toLocaleDateString()}
                            </p>
                            {request.admin_notes && (
                              <div className="mt-2 p-2 bg-slate-800/50 rounded border border-slate-700">
                                <p className="text-xs text-slate-400">
                                  <MessageSquare className="inline h-3 w-3 mr-1" />
                                  Response: <span className="text-slate-300">{request.admin_notes}</span>
                                </p>
                              </div>
                            )}
                          </div>
                          {request.status === 'pending' && (
                            <Button
                              onClick={() => handleDeleteFeatureRequest(request.id)}
                              size="sm"
                              variant="ghost"
                              className="text-slate-500 hover:text-red-400"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      </div>
                    ))
                  )}
                </div>
              )}
            </CardContent>
          )}
        </Card>
      </div>
    </div>
  );
}
